import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  Sparkles, 
  Brain, 
  TrendingUp, 
  ChevronRight,
  ArrowUpRight,
  Activity
} from 'lucide-react';

const AIInsightPlaceholder = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  
  return (
    <div className="p-5 rounded-xl overflow-hidden relative border shadow-sm
      bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50
      dark:from-indigo-900/20 dark:via-blue-900/20 dark:to-purple-900/20
      border-indigo-200 dark:border-indigo-800">
      
      {/* Background svg elements for visual interest */}
      <div className="absolute inset-0 overflow-hidden opacity-10 pointer-events-none">
        <svg width="100%" height="100%" className="absolute top-0 left-0">
          <circle cx="5%" cy="10%" r="40" fill={isDark ? "#818cf8" : "#4f46e5"} />
          <circle cx="95%" cy="50%" r="60" fill={isDark ? "#c084fc" : "#8b5cf6"} />
          <circle cx="40%" cy="90%" r="50" fill={isDark ? "#60a5fa" : "#3b82f6"} />
          
          {/* Neural network style lines */}
          <line x1="5%" y1="10%" x2="40%" y2="90%" stroke={isDark ? "#818cf8" : "#4f46e5"} strokeWidth="2" strokeDasharray="5,5" />
          <line x1="95%" y1="50%" x2="40%" y2="90%" stroke={isDark ? "#c084fc" : "#8b5cf6"} strokeWidth="2" strokeDasharray="5,5" />
          <line x1="5%" y1="10%" x2="95%" y2="50%" stroke={isDark ? "#60a5fa" : "#3b82f6"} strokeWidth="2" strokeDasharray="5,5" />
        </svg>
      </div>
      
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center mb-4">
            <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 mr-3">
              <Brain className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h3 className="font-bold text-lg text-gray-900 dark:text-white">
              {t('standaloneDashboard.aiInsight.title')}
            </h3>
          </div>
          
          {/* Coming soon badge */}
          <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300">
            <Sparkles className="h-3.5 w-3.5 mr-1" />
            {t('standaloneDashboard.comingSoon')}
          </div>
        </div>
        
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {t('standaloneDashboard.aiInsight.description')}
        </p>
        
        {/* Feature previews */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <FeatureCard 
            icon={TrendingUp}
            title={t('standaloneDashboard.aiInsight.features.progressTitle')}
            description={t('standaloneDashboard.aiInsight.features.progressDesc')}
          />
          
          <FeatureCard 
            icon={Activity}
            title={t('standaloneDashboard.aiInsight.features.recommendationsTitle')}
            description={t('standaloneDashboard.aiInsight.features.recommendationsDesc')}
          />
          
          <FeatureCard 
            icon={ArrowUpRight}
            title={t('standaloneDashboard.aiInsight.features.goalsTitle')}
            description={t('standaloneDashboard.aiInsight.features.goalsDesc')}
          />
        </div>
        
        {/* Mailing list signup button - future implementation */}
        <button 
          className="w-full flex items-center justify-center px-4 py-2 bg-indigo-100 dark:bg-indigo-900/50 hover:bg-indigo-200 dark:hover:bg-indigo-800/50 text-indigo-800 dark:text-indigo-300 rounded-lg transition-colors"
          onClick={() => window.alert(t('standaloneDashboard.aiInsight.notifyAlert'))}
        >
          {t('standaloneDashboard.aiInsight.notifyButton')}
          <ChevronRight className="h-4 w-4 ml-1" />
        </button>
      </div>
    </div>
  );
};

// Feature card component
const FeatureCard = ({ icon: Icon, title, description }) => {
  const { isDark } = useTheme();
  
  return (
    <div className={`p-3 rounded-lg ${
      isDark ? 'bg-white/5' : 'bg-white/40'
    }`}>
      <div className="flex items-center mb-2">
        <Icon className="h-4 w-4 mr-2 text-indigo-600 dark:text-indigo-400" />
        <h4 className="font-medium text-sm text-gray-900 dark:text-white">{title}</h4>
      </div>
      <p className="text-xs text-gray-600 dark:text-gray-400">{description}</p>
    </div>
  );
};

export default AIInsightPlaceholder;